import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { FileText, RefreshCw, Save, Send, Trash2, Upload } from 'lucide-react';
import { Alert, Badge, Button, Card, Empty, Field, Form, Loading, PageHeader } from '../components/UI';
import { api, errorText } from '../lib/api';

type Resource = { uuid: string; title: string; type?: string; mime_type?: string; size_bytes?: number; external_url?: string | null };
type Lesson = { id: number; title: string; type: string; position: number; status?: string; content_html?: string; video_url?: string | null; transcript?: string | null; resources?: Resource[] };
type Module = { id: number; title: string; position: number; phase?: string; lessons?: Lesson[] };
type Phase = { id: number; title: string; position: number; modules?: Module[] };
type Curriculum = { id: number; version: string; label?: string | null; status: string; phases?: Phase[] };
type ProgramOption = { uuid: string; title: string };
type LessonForm = { title: string; type: string; content_html: string; video_url: string; transcript: string; position: number; is_required: boolean; allow_download: boolean; status: string; resource_title: string };

const emptyLesson: LessonForm = { title: '', type: 'html', content_html: '<h2>Class objective</h2><p>Write the class content, then list what the learner must do to complete this class.</p>', video_url: '', transcript: '', position: 1, is_required: true, allow_download: true, status: 'draft', resource_title: '' };
const lessonTypes = ['html', 'article', 'text', 'video', 'youtube', 'pdf', 'document', 'reading', 'worksheet', 'slides', 'presentation', 'resource', 'download', 'file', 'lab', 'workshop', 'exercise', 'quiz'];
const materialTypes = ['pdf', 'document', 'slides', 'presentation', 'worksheet', 'reading', 'video', 'resource', 'download', 'file'];

function list<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const wrapped = (data as { data?: unknown })?.data;
  return Array.isArray(wrapped) ? (wrapped as T[]) : [];
}

export function ContentStudioPage() {
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programUuid, setProgramUuid] = useState('');
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [curriculumId, setCurriculumId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [lesson, setLesson] = useState<LessonForm>({ ...emptyLesson });
  const [resourceFile, setResourceFile] = useState<File | null>(null);
  const [quick, setQuick] = useState<{ lessonId: string; title: string; type: string; file: File | null }>({ lessonId: '', title: '', type: 'pdf', file: null });
  const [newVersion, setNewVersion] = useState('');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadPrograms = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get<unknown>('/admin/programs/options');
      const rows = list<ProgramOption>(r.data);
      setPrograms(rows);
      if (!programUuid && rows[0]) setProgramUuid(rows[0].uuid);
    } catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  };

  const loadCurricula = async () => {
    if (!programUuid) return;
    setLoading(true); setError('');
    try {
      const r = await api.get<Curriculum[]>(`/admin/programs/${programUuid}/curricula`);
      const rows = r.data || [];
      setCurricula(rows);
      const draft = rows.find(c => c.status === 'draft');
      const target = draft || rows[0];
      setCurriculumId(prev => (prev && rows.some(c => String(c.id) === prev) ? prev : target ? String(target.id) : ''));
    } catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  };

  useEffect(() => { void loadPrograms(); }, []);
  useEffect(() => { setCurriculumId(''); setModuleId(''); setEditingId(null); void loadCurricula(); }, [programUuid]);

  const selected = curricula.find(c => String(c.id) === curriculumId) || null;
  const draft = curricula.find(c => c.status === 'draft') || null;
  const published = curricula.find(c => c.status === 'published') || null;
  const isDraft = selected?.status === 'draft';

  const modules = useMemo(() => (selected?.phases || []).flatMap(p => (p.modules || []).map(m => ({ ...m, phase: p.title }))), [selected]);
  const currentModule = modules.find(m => String(m.id) === moduleId) || modules[0] || null;
  const moduleLessons = currentModule?.lessons || [];

  useEffect(() => { if (modules[0]) setModuleId(String(modules[0].id)); else setModuleId(''); }, [curriculumId, curricula.length]);
  useEffect(() => { setModuleId(modules[0] ? String(modules[0].id) : ''); }, [programUuid, selected?.id]);
  useEffect(() => { setQuick(q => ({ ...q, lessonId: '' })); }, [moduleId, curriculumId, programUuid]);

  const suggestedVersion = (() => {
    const base = (published?.version || selected?.version || '1.0');
    const parts = base.split('.');
    const last = Number(parts[parts.length - 1]);
    if (!Number.isFinite(last)) return `${base}.1`;
    parts[parts.length - 1] = String(last + 1);
    return parts.join('.');
  })();

  const createDraft = async () => {
    setBusy('draft'); setError('');
    try {
      await api.post(`/admin/programs/${programUuid}/curricula`, {
        version: (newVersion || suggestedVersion),
        label: `Draft from ${published?.version || 'current'}`,
        clone_from_id: published?.id,
      });
      setMessage('Draft version created. You can now edit lessons and materials, then publish.');
      setNewVersion('');
      await loadCurricula();
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  const publishDraft = async () => {
    if (!draft) return;
    setBusy('publish'); setError('');
    try {
      await api.post(`/admin/curricula/${draft.id}/publish`, {});
      setMessage(`Published ${draft.version} to learners.`);
      await loadCurricula();
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  const resetLesson = () => { setEditingId(null); setLesson({ ...emptyLesson }); setResourceFile(null); };

  const editLesson = (l: Lesson) => {
    setEditingId(l.id);
    setLesson({ title: l.title, type: l.type, content_html: l.content_html || '', video_url: l.video_url || '', transcript: l.transcript || '', position: l.position || 1, is_required: true, allow_download: true, status: l.status || 'draft', resource_title: '' });
    setResourceFile(null);
  };

  const saveLesson = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentModule) { setError('Select a module first.'); return; }
    setBusy('lesson'); setError('');
    try {
      if (resourceFile) {
        const form = new FormData();
        form.append('title', lesson.title);
        form.append('slug', lesson.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
        form.append('type', lesson.type);
        form.append('position', String(lesson.position || 1));
        if (lesson.content_html) form.append('content_html', lesson.content_html);
        if (lesson.video_url) form.append('video_url', lesson.video_url);
        if (lesson.transcript) form.append('transcript', lesson.transcript);
        form.append('is_required', lesson.is_required ? '1' : '0');
        form.append('allow_download', lesson.allow_download ? '1' : '0');
        form.append('status', lesson.status);
        form.append('resource_title', lesson.resource_title || lesson.title);
        form.append('resource_file', resourceFile);
        if (editingId) await api.post(`/admin/lessons/${editingId}`, form);
        else await api.post(`/admin/modules/${currentModule.id}/lessons`, form);
      } else {
        const payload = {
          title: lesson.title, type: lesson.type, video_url: lesson.video_url || null, transcript: lesson.transcript || null,
          content_html: lesson.content_html || null, duration_seconds: 0, position: Number(lesson.position || 1),
          is_required: lesson.is_required, allow_download: lesson.allow_download, status: lesson.status,
          slug: lesson.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        };
        if (editingId) await api.patch(`/admin/lessons/${editingId}`, payload);
        else await api.post(`/admin/modules/${currentModule.id}/lessons`, payload);
      }
      setMessage(editingId ? 'Lesson updated.' : 'Lesson added to the draft.');
      resetLesson();
      await loadCurricula();
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  const deleteLesson = async (l: Lesson) => {
    setBusy('lesson-del-' + l.id); setError('');
    try {
      await api.delete(`/admin/lessons/${l.id}`);
      setMessage('Lesson deleted.');
      if (editingId === l.id) resetLesson();
      await loadCurricula();
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  const addMaterial = async (e: FormEvent) => {
    e.preventDefault();
    if (!quick.lessonId || !quick.file) { setError('Select a lesson and a file.'); return; }
    setBusy('material'); setError('');
    try {
      const form = new FormData();
      form.append('lesson_id', quick.lessonId);
      form.append('title', quick.title || quick.file.name);
      form.append('type', quick.type);
      form.append('is_downloadable', '1');
      form.append('file', quick.file);
      await api.post('/admin/resources', form);
      setMessage('Material attached to the lesson. Learners can open it from the class.');
      setQuick({ lessonId: '', title: '', type: 'pdf', file: null });
      await loadCurricula();
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  const deleteMaterial = async (r: Resource) => {
    setBusy('material-del-' + r.uuid); setError('');
    try {
      await api.delete(`/admin/lesson-resources/${r.uuid}`);
      setMessage('Material removed.');
      await loadCurricula();
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Content studio" title="Course content manager" description="Author lessons and attach study materials (PDF, slides, video) for each class. Published programmes are edited through a draft version, then published to learners."
        actions={<div className="row-actions"><Button kind="secondary" onClick={() => void loadCurricula()}><RefreshCw /> Refresh</Button>{(lesson.title || editingId) && <Button kind="secondary" onClick={resetLesson}>Clear form</Button>}</div>} />
      {message && <Alert type="success" onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <div className="toolbar">
          <Field label="Programme">
            <select value={programUuid} onChange={e => setProgramUuid(e.target.value)}>
              {programs.map(p => <option key={p.uuid} value={p.uuid}>{p.title}</option>)}
            </select>
          </Field>
          <Field label="Curriculum version">
            <select value={curriculumId} onChange={e => setCurriculumId(e.target.value)}>
              {curricula.map(c => <option key={c.id} value={c.id}>v{c.version} · {c.status}</option>)}
            </select>
          </Field>
          <Field label="Module">
            <select value={moduleId} onChange={e => setModuleId(e.target.value)}>
              {modules.map(m => <option key={m.id} value={m.id}>{m.phase} · {m.title}</option>)}
            </select>
          </Field>
          {isDraft
            ? <Button busy={busy === 'publish'} onClick={() => void publishDraft()}><Send /> Publish v{selected?.version}</Button>
            : <div className="row-actions"><input value={newVersion} onChange={e => setNewVersion(e.target.value)} placeholder={suggestedVersion} style={{ width: 90 }} /><Button busy={busy === 'draft'} onClick={() => void createDraft()}>Create draft version</Button></div>}
        </div>
        <p className="muted">{isDraft ? `Editing draft v${selected?.version}. Publish when ready; learners keep the published version until you do.` : `This programme is ${selected?.status || 'published'}. Lessons are read-only here — create a draft version to edit, or attach materials directly below.`}</p>
      </Card>

      <div className="grid two">
        <Card title={editingId ? 'Edit lesson' : 'Add a lesson'} >
          <Form onSubmit={saveLesson} busy={busy === 'lesson'} submitLabel={editingId ? 'Save lesson' : 'Add lesson'}>
            <div className="form-grid">
              <Field label="Title"><input value={lesson.title} onChange={e => setLesson({ ...lesson, title: e.target.value })} required /></Field>
              <Field label="Type"><select value={lesson.type} onChange={e => setLesson({ ...lesson, type: e.target.value })}>{lessonTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
              <Field label="Position"><input type="number" min={1} value={lesson.position} onChange={e => setLesson({ ...lesson, position: Number(e.target.value) })} /></Field>
              <Field label="Status"><select value={lesson.status} onChange={e => setLesson({ ...lesson, status: e.target.value })}><option value="draft">draft</option><option value="published">published</option><option value="review">review</option></select></Field>
              <Field label="Video URL (optional)" wide><input value={lesson.video_url} onChange={e => setLesson({ ...lesson, video_url: e.target.value })} placeholder="https://..." /></Field>
              <Field label="Class content (HTML)" wide><textarea rows={6} value={lesson.content_html} onChange={e => setLesson({ ...lesson, content_html: e.target.value })} /></Field>
              <Field label="Transcript (optional)" wide><textarea rows={3} value={lesson.transcript} onChange={e => setLesson({ ...lesson, transcript: e.target.value })} /></Field>
              <Field label="Attach material file (optional)" wide><input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.zip,image/*,video/*" onChange={e => setResourceFile(e.target.files?.[0] || null)} /></Field>
              {resourceFile && <Field label="Material title" wide><input value={lesson.resource_title} onChange={e => setLesson({ ...lesson, resource_title: e.target.value })} placeholder={resourceFile.name} /></Field>}
            </div>
            {!isDraft && <p className="muted"><strong>Heads up:</strong> adding or editing lessons needs a draft version. Attach materials to an existing lesson below instead.</p>}
          </Form>
        </Card>

        <Card title="Class materials">
          <Form onSubmit={addMaterial} busy={busy === 'material'} submitLabel="Attach material">
            <div className="form-grid">
              <Field label="Module" wide>
                <select value={moduleId} onChange={e => setModuleId(e.target.value)}>
                  {modules.length === 0 && <option value="">No modules in this curriculum version</option>}
                  {modules.map(m => <option key={m.id} value={m.id}>{m.phase} · {m.title}</option>)}
                </select>
              </Field>
              <Field label="Lesson" wide>
                <select value={quick.lessonId} onChange={e => setQuick({ ...quick, lessonId: e.target.value })} required disabled={moduleLessons.length === 0}>
                  <option value="">{moduleLessons.length === 0 ? 'No lessons in this module' : 'Select a lesson'}</option>
                  {moduleLessons.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
              </Field>
              <Field label="Material title"><input value={quick.title} onChange={e => setQuick({ ...quick, title: e.target.value })} placeholder="Defaults to file name" /></Field>
              <Field label="Type"><select value={quick.type} onChange={e => setQuick({ ...quick, type: e.target.value })}>{materialTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
              <Field label="File" wide><input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.zip,image/*" onChange={e => setQuick({ ...quick, file: e.target.files?.[0] || null })} required /></Field>
            </div>
          </Form>
          <p className="muted">Choose the module, then the lesson, to attach a material. Materials can be attached to any lesson, published or draft, and appear to learners as class resources.</p>
        </Card>
      </div>

      <Card title={`Lessons in ${currentModule?.title || 'this module'}`}>
        {loading ? <Loading /> : moduleLessons.length === 0 ? <Empty title="No lessons in this module yet" text="Add a lesson above (requires a draft version)." /> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Lesson</th><th>Type</th><th>Status</th><th>Materials</th><th></th></tr></thead>
              <tbody>
                {moduleLessons.map(l => (
                  <tr key={l.id}>
                    <td><strong>{l.title}</strong><small>#{l.position}</small></td>
                    <td>{l.type}</td>
                    <td><Badge tone={l.status || 'draft'}>{l.status || 'draft'}</Badge></td>
                    <td>
                      {(l.resources || []).length === 0 ? <small className="muted">none</small> : (l.resources || []).map(r => (
                        <div key={r.uuid} className="row-actions"><FileText size={14} /><span>{r.title}</span><Button kind="ghost" busy={busy === 'material-del-' + r.uuid} onClick={() => void deleteMaterial(r)}><Trash2 size={13} /></Button></div>
                      ))}
                    </td>
                    <td className="row-actions">
                      <Button kind="ghost" onClick={() => editLesson(l)}>Edit</Button>
                      <Button kind="ghost" busy={busy === 'lesson-del-' + l.id} onClick={() => void deleteLesson(l)}><Trash2 size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <p className="muted"><Upload size={13} /> Tip: use <strong>Class materials</strong> for PDFs, slides and documents, and a <strong>video</strong> lesson type (or a session recording) for class replays.</p>
    </div>
  );
}
